import { CreditRuleCoin } from '@aibees/elephant/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Button, CircularProgress } from '@mui/material';
import { memo, useEffect } from 'react';
import { useHandleUnlockEmails } from '../../hooks/use-handle-unlock-emails';
import * as Styled from './styles';
import * as SX from './sx';
import type { UnlockEmailsProps as Props } from './types';

export const UnlockEmails = memo(
  ({ className, compact, item, setLoading }: Props) => {
    const hover = useBoolean(false);

    const {
      handleReverify,
      handleUpdate,
      hasUpdate,
      needToCheckForUpdate,
      showReverify,
      unlocked,
      loading,
      handleUnlock,
      handleCheckForUpdate,
      creditType,
    } = useHandleUnlockEmails(item);

    useEffect(() => {
      setLoading?.(loading);
    }, [loading]);

    const onClickReverify = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      if (!loading) handleReverify(e);
      hover.onFalse();
    };

    const onClickUnlock = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      if (!loading) handleUnlock(e);
      hover.onFalse();
    };

    const onClickUpdate = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      if (!loading) handleUpdate(e);
      hover.onFalse();
    };

    const onClickCheckForUpdate = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      if (!loading) handleCheckForUpdate(e);
      hover.onFalse();
    };

    const handleMouseLeave = () => {
      hover.onFalse();
    };

    const handleMouseEnter = () => {
      hover.onTrue();
    };

    return (
      <Styled.Wrapper
        className={classnames('UnlockEmails', className)}
        sx={SX.root(!!compact)}
        direction="row"
        justifyContent={compact ? 'unset' : 'space-between'}
        alignItems="center"
        gap={0.5}
      >
        {hover.value && !loading && (
          <CreditRuleCoin type={creditType} sx={SX.creditTag} />
        )}

        {!unlocked && !loading && (
          <Button
            variant="contained"
            color="primary"
            size="xsmall"
            onClick={onClickUnlock}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            Unlock
          </Button>
        )}

        {hasUpdate && !loading && (
          <Button
            variant="contained"
            color="primary"
            size="xsmall"
            onClick={onClickUpdate}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            Update
          </Button>
        )}

        {needToCheckForUpdate && !hasUpdate && !loading && (
          <Button
            variant="contained"
            color="primary"
            size="xsmall"
            onClick={onClickCheckForUpdate}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            Check for Update
          </Button>
        )}

        {showReverify && !(hasUpdate || needToCheckForUpdate) && !loading && (
          <Button
            variant="contained"
            color="primary"
            size="xsmall"
            onClick={onClickReverify}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            Re-Verify
          </Button>
        )}

        {loading && (
          <Button
            variant="contained"
            color="primary"
            size="xsmall"
            onMouseLeave={handleMouseLeave}
          >
            <CircularProgress size={12} sx={SX.loadingIcon} />
          </Button>
        )}
      </Styled.Wrapper>
    );
  }
);

UnlockEmails.displayName = 'UnlockEmails';
